import type {createBrowserRouter, RouteObject} from 'react-router-dom';
import {matchRoutes} from 'react-router-dom';

/**
 * Router-level back trail: for every history entry, the distinct pages passed through to reach it.
 * React-free; `useBackNavigation` reads it through `useSyncExternalStore`.
 */

type DataRouter = ReturnType<typeof createBrowserRouter>;
type RouterState = DataRouter['state'];

interface RouteMatchLike {
  params: Record<string, string | undefined>;
  route: {path?: string; handle?: AppRouteHandle};
}

/** `page` is a Back target; `form` (create/edit/clone/guided entry) and `transient` (auth, redirects) never are. */
export type BackNavigationKind = 'page' | 'form' | 'transient';

export interface AppRouteHandle {
  backNavigation: BackNavigationKind;
}

export interface TrailEntry {
  key: string;
  idx?: number;
  pageKey: string;
  path: string;
}

interface EntryRecord extends TrailEntry {
  isBackTargetEligible: boolean;
  trail: TrailEntry[];
}

export interface BackTarget {
  entry: TrailEntry;
  /** History delta to the origin entry, set only when its index is verified. */
  delta?: number;
}

interface PersistedTrail {
  records: [string, EntryRecord][];
  keyByIdx: Record<number, string>;
}

const STORAGE_KEY = 'proact.backTrail.v2';
const TRAIL_CAP = 20;
const RECORD_CAP = 200;
const INITIAL_LOCATION_KEY = 'default';
const TAB_PARAMS = new Set(['view', 'tab', 'section']);

const listeners = new Set<() => void>();
let records: Map<string, EntryRecord> | undefined;
let keyByIdx: Record<number, string> = {};
let revision = 0;
let routes: RouteObject[] | undefined;
let lastKey: string | undefined;

const routePattern = (matches: RouteMatchLike[]): string =>
  `/${matches
    .flatMap(match => (match.route.path ?? '').split('/'))
    .filter(Boolean)
    .join('/')}`;

/** Page identity: route pattern + params, minus tab params so tab switches stay one page. */
export const pageKey = (matches: RouteMatchLike[]): string => {
  const params = matches[matches.length - 1]?.params ?? {};
  const identity = Object.keys(params)
    .filter(name => !TAB_PARAMS.has(name) && params[name] !== undefined)
    .sort()
    .map(name => `${name}=${params[name]}`)
    .join('&');
  return `${routePattern(matches)}?${identity}`;
};

export const getTrackedRoutes = (): RouteObject[] | undefined => routes;

/** Page identity of an arbitrary path; undefined until a router is tracked. */
export const pageKeyForPath = (
  path: string,
  routeTable: RouteObject[] | undefined
): string | undefined => {
  if (!routeTable) return undefined;
  const matches = matchRoutes(routeTable, path);
  return matches ? pageKey(matches) : undefined;
};

const readStorage = (): Map<string, EntryRecord> => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as PersistedTrail;
    keyByIdx = parsed.keyByIdx ?? {};
    return new Map(parsed.records);
  } catch {
    keyByIdx = {};
    return new Map();
  }
};

const getRecords = (): Map<string, EntryRecord> => {
  records ??= readStorage();
  return records;
};

const writeStorage = (): void => {
  try {
    const persisted: PersistedTrail = {
      records: Array.from(getRecords().entries()),
      keyByIdx,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // Storage full or blocked: the in-memory map stays authoritative.
  }
};

const currentHistoryIdx = (): number | undefined => {
  const idx = (window.history.state as {idx?: unknown} | null)?.idx;
  return typeof idx === 'number' ? idx : undefined;
};

const putRecord = (record: EntryRecord): void => {
  const map = getRecords();
  map.delete(record.key);
  map.set(record.key, record);
  if (map.size > RECORD_CAP) {
    const oldest = map.keys().next().value;
    if (oldest !== undefined) map.delete(oldest);
  }
  if (record.idx !== undefined) keyByIdx[record.idx] = record.key;
  writeStorage();
};

const emit = (): void => {
  revision += 1;
  listeners.forEach(listener => listener());
};

const describeLocation = (state: RouterState): Omit<EntryRecord, 'trail'> => {
  const {location, matches} = state;
  return {
    key: location.key,
    idx: currentHistoryIdx(),
    pageKey: pageKey(matches),
    isBackTargetEligible: !matches.some(
      match => (match.route.handle?.backNavigation ?? 'page') !== 'page'
    ),
    path: `${location.pathname}${location.search}${location.hash}`,
  };
};

const toEntry = ({key, idx, pageKey: page, path}: EntryRecord): TrailEntry => ({
  key,
  idx,
  pageKey: page,
  path,
});

const nextTrail = (
  current: Omit<EntryRecord, 'trail'>,
  previous: EntryRecord | undefined,
  action: RouterState['historyAction']
): TrailEntry[] => {
  if (!previous) return [];
  let base = previous.trail;
  if (
    action === 'PUSH' &&
    previous.pageKey !== current.pageKey &&
    previous.isBackTargetEligible
  ) {
    base = [...base, toEntry(previous)];
  }
  const revisit = base.findIndex(entry => entry.pageKey === current.pageKey);
  if (revisit >= 0) base = base.slice(0, revisit);
  return base.slice(-TRAIL_CAP);
};

const seed = (state: RouterState): void => {
  const current = describeLocation(state);
  lastKey = current.key;
  if (current.key === INITIAL_LOCATION_KEY || !getRecords().has(current.key)) {
    putRecord({...current, trail: []});
  }
  emit();
};

const onRouterChange = (state: RouterState): void => {
  if (state.location.key === lastKey) return;
  const current = describeLocation(state);
  const previous = lastKey ? getRecords().get(lastKey) : undefined;
  lastKey = current.key;

  if (state.historyAction !== 'POP') {
    putRecord({
      ...current,
      trail: nextTrail(current, previous, state.historyAction),
    });
  } else if (!getRecords().has(current.key)) {
    putRecord({...current, trail: []});
  }
  emit();
};

export const trackBackTrail = (router: DataRouter): (() => void) => {
  routes = router.routes as RouteObject[];
  seed(router.state);
  return router.subscribe(onRouterChange);
};

export const resolveBack = (key: string): BackTarget | undefined => {
  const record = getRecords().get(key);
  const entry = record?.trail[record.trail.length - 1];
  if (!record || !entry) return undefined;

  const {idx: originIdx} = entry;
  const {idx: currentIdx} = record;
  if (
    originIdx !== undefined &&
    currentIdx !== undefined &&
    originIdx < currentIdx &&
    keyByIdx[originIdx] === entry.key
  ) {
    return {entry, delta: originIdx - currentIdx};
  }
  return {entry};
};

/** Back target of the router's committed location; undefined when untracked or the trail is empty. */
export const resolveCurrentBack = (): BackTarget | undefined =>
  lastKey === undefined ? undefined : resolveBack(lastKey);

/** Whether the router's committed entry is a Back target; true when untracked or unknown. */
export const isCurrentEntryBackTarget = (): boolean =>
  lastKey === undefined ||
  (getRecords().get(lastKey)?.isBackTargetEligible ?? true);

export const subscribeBackTrail = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getBackTrailSnapshot = (): number => revision;
